import type { SpendModelPoint, Tool } from '@ace-tracker/shared';
import { SPEND_METRIC_INFO } from '../metricInfo';
import { TOOL_LABELS } from './Sidebar';
import { TableCard } from './TableCard';

const DASH = '—';
const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

interface RateRow {
  key: string;
  label: string;
  tool: Tool;
  subtotal: boolean;
  fallback: boolean;
  tokens: SpendModelPoint['tokens'];
  byType: SpendModelPoint['byType'];
  cost: number;
}

function perMillion(cost: number, tokens: number): number | null {
  return tokens > 0 ? (cost / tokens) * 1_000_000 : null;
}

/** Effective rate, average input/output rates paid, and saving against sending every context token as fresh input. */
function rates(row: RateRow) {
  const { input, output, cacheRead, cacheWrite } = row.tokens;
  const total = input + output + cacheRead + cacheWrite;
  const inputRate = perMillion(row.byType.input, input);
  const uncached = inputRate === null ? null : row.byType.output + ((input + cacheRead + cacheWrite) * inputRate) / 1_000_000;
  return {
    total,
    effective: perMillion(row.cost, total),
    inputRate,
    outputRate: perMillion(row.byType.output, output),
    saving: uncached && uncached > 0 ? 1 - row.cost / uncached : null,
  };
}

function toRows(byModel: SpendModelPoint[]): RateRow[] {
  const rows: RateRow[] = [];
  const tools = [...new Set(byModel.map((m) => m.tool as Tool))];
  for (const tool of tools) {
    const models = byModel.filter((m) => m.tool === tool);
    for (const m of models) {
      rows.push({ key: `${tool}:${m.model}`, label: m.model, tool, subtotal: false, fallback: m.pricedAs === null, tokens: m.tokens, byType: m.byType, cost: m.cost });
    }
    if (models.length > 1) {
      const sum = (pick: (m: SpendModelPoint) => SpendModelPoint['tokens']) =>
        models.reduce(
          (acc, m) => {
            const v = pick(m);
            return { input: acc.input + v.input, output: acc.output + v.output, cacheRead: acc.cacheRead + v.cacheRead, cacheWrite: acc.cacheWrite + v.cacheWrite };
          },
          { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
        );
      rows.push({
        key: `${tool}:all`,
        label: `All ${TOOL_LABELS[tool]} models`,
        tool,
        subtotal: true,
        fallback: false,
        tokens: sum((m) => m.tokens),
        byType: sum((m) => m.byType),
        cost: models.reduce((acc, m) => acc + m.cost, 0),
      });
    }
  }
  return rows;
}

/**
 * Effective price per million tokens (E1.9): what usage actually cost per
 * token after caching. Subtotals are per tool — tokens are never summed across tools.
 */
export function EffectiveRateTable({ byModel, money }: { byModel: SpendModelPoint[]; money: (value: number) => string }) {
  const rows = toRows(byModel);
  const rate = (value: number | null) => (value === null ? DASH : money(value));
  return (
    <TableCard
      title="Effective price per million tokens"
      info={SPEND_METRIC_INFO.effectiveRate}
      empty={rows.length === 0}
      emptyText="No token usage in this range."
    >
      <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
        <tr>
          <th className="pb-2.5 pr-3">Model</th>
          <th className="pb-2.5 pr-3">Tool</th>
          <th className="pb-2.5 pr-3 text-right">Tokens</th>
          <th className="pb-2.5 pr-3 text-right">Cost</th>
          <th className="pb-2.5 pr-3 text-right">Effective / M</th>
          <th className="pb-2.5 pr-3 text-right">Input paid / M</th>
          <th className="pb-2.5 pr-3 text-right">Output paid / M</th>
          <th className="pb-2.5 text-right">Saved by cache</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const r = rates(row);
          return (
            <tr key={row.key} className={`border-b border-border last:border-0 ${row.subtotal ? 'bg-surface-alt font-medium' : ''}`}>
              <td className="py-2.5 pr-3 text-text-default">
                {row.label}
                {row.fallback && ' '}
                {row.fallback && (
                  <span className="ml-1.5 inline-flex items-center gap-1 rounded-input bg-warn-bg px-1.5 py-0.5 text-[11px] text-text-default">
                    <i aria-hidden="true" className="fa-solid fa-triangle-exclamation text-warn-ink" />
                    Fallback rate
                  </span>
                )}
              </td>
              <td className="py-2.5 pr-3 text-text-muted">{TOOL_LABELS[row.tool]}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums" title={r.total.toLocaleString()}>
                {compact.format(r.total)}
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{money(row.cost)}</td>
              <td className="py-2.5 pr-3 text-right font-medium tabular-nums text-text-default">{rate(r.effective)}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{rate(r.inputRate)}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{rate(r.outputRate)}</td>
              <td className="py-2.5 text-right tabular-nums">
                {r.saving === null ? DASH : r.saving < 0 ? `${(r.saving * 100).toFixed(0)}% (cache writes cost more)` : `${(r.saving * 100).toFixed(0)}%`}
              </td>
            </tr>
          );
        })}
      </tbody>
    </TableCard>
  );
}
